import { CdkVirtualScrollableElement } from '@angular/cdk/scrolling';
import { AsyncPipe } from '@angular/common';
import {
  afterRenderEffect,
  Component,
  computed,
  DOCUMENT,
  effect,
  ElementRef,
  inject,
  signal,
  Signal,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon, MatIconRegistry } from '@angular/material/icon';
import { MatListItem, MatListItemIcon, MatNavList } from '@angular/material/list';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { MatToolbar } from '@angular/material/toolbar';
import { MatTooltip } from '@angular/material/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet, UrlTree } from '@angular/router';
import { ExternalLinkModule } from '@psu-pennstateit-swe/utils/angular';
import { CdnModule } from '@psu-pennstateit-swe/utils/cdn';
import { ScreenSizeDirective, ScreenSizeService } from '@psu-pennstateit-swe/utils/responsive';
import { filter, map } from 'rxjs';
import { CalendarsService } from './core/calendars/calendars.service';
import { UsersService } from './core/users/users.service';
import { APP_CONFIG } from './properties.model';
import { SearchbarComponent } from './shared/searchbar/searchbar.component';

/** Represents the three user-selectable theme modes: explicit light, explicit dark, or system-driven auto. */
export type ThemeMode = 'light' | 'dark' | 'auto';

@Component({
  selector: 'iwah-root',
  imports: [
    CdnModule,
    MatToolbar,
    ExternalLinkModule,
    MatIcon,
    MatListItem,
    MatListItemIcon,
    MatNavList,
    MatSidenav,
    MatSidenavContainer,
    MatSidenavContent,
    RouterLink,
    RouterOutlet,
    ScreenSizeDirective,
    MatIconButton,
    RouterLinkActive,
    SearchbarComponent,
    AsyncPipe,
    CdkVirtualScrollableElement,
    MatTooltip,
    MatButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  mainContentAreaElement = viewChild.required<ElementRef<HTMLElement>>('mainContentArea');
  searchbar = viewChild<SearchbarComponent>('searchbarComponent');

  private readonly appConfig = inject(APP_CONFIG);
  readonly developmentFeatureRoutesEnabled = this.appConfig.developmentFeatureRoutesEnabled;

  /** Logo variants — the dark-theme logo uses reversed (white) colors for contrast on dark backgrounds. */
  readonly lightThemeLogoPath = 'img/PSU_HOR_RGB_2C.svg';
  readonly darkThemeLogoPath = 'img/PSU_HOR_REV_RGB_2C.svg';

  /** The currently selected theme mode; defaults to 'auto' so new users follow their OS preference. */
  themeMode = signal<ThemeMode>('auto');
  /** Tracks the OS-level dark-mode media query result, updated via a `change` event listener. */
  private readonly systemPrefersDark = signal(false);

  /**
   * Resolves whether dark theme is currently active.
   * When mode is 'auto', defers to the OS preference; otherwise uses the explicit selection.
   */
  readonly isDarkThemeActive = computed(() => {
    const mode = this.themeMode();
    if (mode === 'auto') {
      return this.systemPrefersDark();
    }
    return mode === 'dark';
  });

  /** Maps the active theme mode to its icon name and display label */
  private readonly themeDisplay = computed(() => {
    switch (this.themeMode()) {
      case 'light':
        return { icon: 'white-balance-sunny', label: 'Light' };
      case 'dark':
        return { icon: 'weather-night', label: 'Dark' };
      case 'auto':
        return { icon: 'theme-light-dark', label: 'Auto' };
    }
  });

  /** Toolbar icon name for the current theme mode, derived from `themeDisplay`. */
  readonly themeIcon = computed(() => this.themeDisplay().icon);
  /** Display label for the current theme mode, derived from `themeDisplay`. */
  readonly themeLabel = computed(() => this.themeDisplay().label);

  /** Switches the logo between light and dark variants whenever the effective theme changes. */
  readonly logoPath = computed(() => (this.isDarkThemeActive() ? this.darkThemeLogoPath : this.lightThemeLogoPath));
  isNotHomeRoute$;
  isMobileSearchVisible = signal(false);
  isSidenavOpen = signal(false);
  isSidenavOpening = signal(false);
  isMobileSidenavOpen = computed(
    () => (this.isSidenavOpen() || this.isSidenavOpening()) && this.isHandsetAndPortrait(),
  );
  isDarkThemeEnabled = signal(false);

  readonly isHandsetAndPortrait: Signal<boolean>;
  private readonly screenSizeService = inject(ScreenSizeService);
  private readonly matIconRegistry = inject(MatIconRegistry);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly calendarsService = inject(CalendarsService);
  private readonly usersService = inject(UsersService);
  private navigationEnd = toSignal(this.router.events.pipe(filter((event) => event instanceof NavigationEnd)));
  private hasCompletedInitialNavigation = this.router.navigated;
  private previousNavigationPath = this.navigationPath(this.router.url);
  /** localStorage key used to persist the user's theme preference across sessions. */
  private readonly themeStorageKey = 'iwah-theme-mode';

  constructor() {
    this.matIconRegistry.addSvgIconSet(this.domSanitizer.bypassSecurityTrustResourceUrl('mdi.svg'));
    this.matIconRegistry.addSvgIcon(
      'folder-arrow-bottom-right-outline',
      this.domSanitizer.bypassSecurityTrustResourceUrl('images/folder-arrow-bottom-right-outline.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'sync-arrow-left',
      this.domSanitizer.bypassSecurityTrustResourceUrl('images/sync-arrow-left.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'sync-arrow-right',
      this.domSanitizer.bypassSecurityTrustResourceUrl('images/sync-arrow-right.svg'),
    );

    this.isHandsetAndPortrait = computed(
      () => this.screenSizeService.isHandset() && this.screenSizeService.isPortrait(),
    );

    this.isNotHomeRoute$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => event.url !== '/'),
    );

    this.initializeTheme();

    effect(() => {
      this.applyTheme(this.isDarkThemeActive());
    });

    this.calendarsService.getCalendarsResource(); // Cache calendars data
    this.usersService.getUserEnvironmentsResource(); // Cache user environments data (do they have prod/test access?)

    effect(() => {
      this.screenSizeService.currentScreenSize();
      this.navigationEnd();
      //closes search bar on mobile view if it's open, but there is no search term/the url changes
      untracked(() => {
        this.checkAndCloseMobileSearch();
      });
    });

    afterRenderEffect({
      write: () => {
        const navigationEvent = this.navigationEnd();
        if (!navigationEvent) {
          return;
        }
        const currentPath = this.navigationPath(navigationEvent.urlAfterRedirects);
        const pathChanged = currentPath !== this.previousNavigationPath;
        this.previousNavigationPath = currentPath;

        if (!this.hasCompletedInitialNavigation) {
          this.hasCompletedInitialNavigation = true;
          return;
        }
        untracked(() => {
          this.focusPageAfterNavigation(pathChanged);
        });
      },
    });
  }

  onSidenavOpenedStart() {
    this.isSidenavOpening.set(true);
  }

  onSidenavOpenedChange(isOpen: boolean) {
    this.isSidenavOpen.set(isOpen);
    if (!isOpen) {
      this.isSidenavOpening.set(false);
    }
  }

  skipToMain(event: MouseEvent) {
    if (event.button !== 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }
    event.preventDefault();
    this.focusMainContent();
  }

  /** Updates the active theme mode and persists the choice; the effect in the constructor applies the DOM change. */
  setTheme(mode: ThemeMode) {
    this.themeMode.set(mode);
    this.persistThemePreference(mode);
  }

  checkAndCloseMobileSearch() {
    const searchbar = this.searchbar();
    const term = searchbar?.searchBarForm.controls.searchBarField.value ?? '';
    if (this.isMobileSearchVisible() && term.length === 0) {
      this.isMobileSearchVisible.set(false);
      searchbar?.quickResults.closePanel();
    }
  }

  private focusMainContent() {
    const main = this.mainContentAreaElement().nativeElement;
    const heading = main.querySelector<HTMLHeadingElement>('h1[tabindex="-1"]');
    (heading ?? main).focus();
  }

  private navigationPath(url: string): string {
    // Query and fragment updates do not represent a new page context.
    return this.router.serializeUrl(new UrlTree(this.router.parseUrl(url).root));
  }

  private focusPageAfterNavigation(pathChanged: boolean) {
    const main = this.mainContentAreaElement().nativeElement;
    const focusedElement = this.document.activeElement;
    if (focusedElement !== main && main.contains(focusedElement)) {
      // Routed tabs change paths but must retain focus for continued keyboard navigation.
      const tab = focusedElement?.closest('[role="tab"]');
      const tabList = tab?.closest('[role="tablist"]');
      if (!pathChanged || (tabList && main.contains(tabList))) {
        return;
      }
    }
    // Reused folder routes retain View Parent's DOM, but a different folder needs heading focus.
    this.focusMainContent();
  }

  /**
   * Bootstraps the theme system on app load:
   * 1. Registers the OS dark-mode media query and keeps `systemPrefersDark` in sync.
   * 2. Restores any previously persisted preference, falling back to 'auto'.
   * 3. Immediately applies the resolved theme so there is no flash of wrong theme.
   */
  private initializeTheme() {
    const mediaQuery = this.document.defaultView?.matchMedia('(prefers-color-scheme: dark)');
    if (mediaQuery) {
      this.systemPrefersDark.set(mediaQuery.matches);
      mediaQuery.addEventListener('change', (e) => {
        this.systemPrefersDark.set(e.matches);
      });
    }

    const storedPreference = this.getStoredThemePreference();
    this.themeMode.set(storedPreference ?? 'auto');
  }

  /**
   * Applies the theme by toggling the `dark` attribute on `<html>`.
   * The Material theme and global CSS target this attribute to switch color tokens.
   */
  private applyTheme(isDark: boolean) {
    if (isDark) {
      this.document.documentElement.setAttribute('dark', '');
      return;
    }
    this.document.documentElement.removeAttribute('dark');
  }

  /** Reads and validates the stored theme preference, returning null for unrecognized values. */
  private getStoredThemePreference(): ThemeMode | null {
    const preference = this.document.defaultView?.localStorage.getItem(this.themeStorageKey);
    if (preference === 'light' || preference === 'dark' || preference === 'auto') {
      return preference;
    }
    return null;
  }

  /** Writes the selected mode to localStorage so it survives page refreshes. */
  private persistThemePreference(mode: ThemeMode) {
    this.document.defaultView?.localStorage.setItem(this.themeStorageKey, mode);
  }
}
